import { useContext, useMemo, useState } from 'react';
import { Metric, ProductPPG } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import KPISelector, { KPIItem } from './KPISelector';
import { handleMetricInputChange, isValidMetricInput } from '../../tpoHelperMethods';
import { Tooltip } from 'antd';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import MetricCell from './MetricSection/MetricCell';

const SpendDetails = ({ promoSyncId }: { promoSyncId: string }) => {
    const {
        updateSelectedEventProperty,
        selectedEventInfo,
        viewMode,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoConfigDictionary,
    } = useContext(TPODataContext);
    const combinedViewMode = viewMode || isTpmTab || isCurrentPromoFormDisabled;
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const [editingCell, setEditingCell] = useState<{ metricId: number; ppgName: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const [warningMessage, setWarningMessage] = useState<string>('');

    const currentPromo = selectedEventInfo?.currentPromoTactic;

    const isInputDisabled = (metricId: number) =>
        !promoConfigDictionary.promoMetricMapping?.[Number(currentPromo?.promoType)]?.spendMetrics?.[metricId]
            ?.editable;

    const spendDetailKPIs: KPIItem[] = useMemo(() => {
        const kpis =
            currentPromo?.ppgs[0]?.spendDetails.map((m: Metric) => ({
                value: m.id?.toString(),
                label: m.name,
                isSelected: m.isSelected,
            })) || [];

        const orderMap: Record<number, number> = Object.entries(promoConfigDictionary?.spendMetrics || {}).reduce(
            (acc, [key, value]) => {
                acc[value.id] = value.orderId ?? 0;
                return acc;
            },
            {} as Record<number, number>,
        );
        return kpis.sort((a, b) => {
            const orderA = orderMap[Number(a.value)] ?? 0;
            const orderB = orderMap[Number(b.value)] ?? 0;
            return orderA - orderB;
        });
    }, [currentPromo?.ppgs, promoConfigDictionary?.spendMetrics]);

    const metricKPIsForSelector: KPIItem[] = useMemo(() => {
        if (currentPromo?.promoType) {
            const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo.promoType]?.spendMetrics;
            const kpis =
                currentPromo?.ppgs[0]?.spendDetails.map((m: Metric) => ({
                    value: m.id?.toString(),
                    label: m.name,
                    isSelected: m.isSelected,
                    groupName: avlMetrics[m.id]?.groupName || '',
                })) || [];
            const allGroups = Array.from(new Set(kpis.map((kpi) => kpi.groupName)));

            return allGroups.sort().map((groupName) => ({
                value: groupName,
                label: groupName,
                isSelected: kpis.some((kpi) => kpi.groupName === groupName && kpi.isSelected),
            }));
        }
        return [];
    }, [spendDetailKPIs]);

    const handleInputChange = (metricId: number, productName: string, value: string) => {
        if (!selectedEventInfo?.currentPromoTactic) return;

        const updatedPPGs: any = handleMetricInputChange(
            promoConfigDictionary,
            selectedEventInfo,
            metricId,
            productName,
            value,
            false,
        );

        if (updatedPPGs)
            // Save changes
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                ppgs: updatedPPGs,
                hasChanges: true,
            });
    };

    const onMetricKPISelectHandler = (updatedGroups: string[]) => {
        // `updatedGroups` contains selected groupNames
        const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo?.promoType || 1]?.spendMetrics;
        selectedEventInfo?.currentPromoTactic.ppgs.forEach((ppg: ProductPPG) => {
            ppg.spendDetails.forEach((metric: Metric) => {
                metric.isSelected = updatedGroups.includes(avlMetrics[metric.id].groupName);
            });
        });

        // enable save button on change of kpis
        if (!viewMode && selectedEventInfo) {
            selectedEventInfo.currentPromoTactic.hasChanges = true;
        }

        if (selectedEventInfo?.currentPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', _.cloneDeep(selectedEventInfo.currentPromoTactic));
        }
    };

    return (
        <>
            <div className=" flex">
                <div className="flex flex-col min-w-[240px]  max-w-[240px] ">
                    {spendDetailKPIs
                        ?.filter((m: KPIItem) => m.isSelected)
                        ?.map((metric: KPIItem) => {
                            return (
                                <Tooltip
                                    placement="top"
                                    color="white"
                                    destroyTooltipOnHide
                                    overlayStyle={{ borderRadius: 4 }}
                                    overlayInnerStyle={{
                                        color: colorConstants.darkGrey,
                                    }}
                                    title={metric.label}
                                >
                                    <div
                                        className={`block truncate h-9 border-l border-r border-b p-2 font-xs border-backgroundGreyAlt1 bg-white
                                          ${isInputDisabled(Number(metric.value)) ? 'text-backgroundGreyAlt2' : ''}
                                        `}
                                    >
                                        {metric.label}
                                    </div>
                                </Tooltip>
                            );
                        })}
                </div>

                <div className={`flex flex-grow overflow-x-scroll  hide-scrollable ${promoSyncId} `}>
                    {currentPromo?.ppgs
                        ?.filter((pm: ProductPPG) => pm.isSelected)
                        ?.map((pm: ProductPPG) => {
                            return (
                                <MetricCell
                                    key={pm.id}
                                    pm={pm}
                                    headerKPIs={spendDetailKPIs}
                                    promoConfigDictionary={promoConfigDictionary}
                                    isInputDisabled={isInputDisabled}
                                    combinedViewMode={combinedViewMode}
                                    editingCell={editingCell}
                                    setEditingCell={setEditingCell}
                                    editValue={editValue}
                                    setEditValue={setEditValue}
                                    isValidMetricInput={isValidMetricInput}
                                    handleInputChange={handleInputChange}
                                    metricType="spendDetails"
                                />
                            );
                        })}
                </div>
            </div>
            <div className=" bg-backgroundGreyAlt  px-2 py-1 relative">
                <div className="inline-flex justify-start items-center gap-4 cursor-pointer">
                    <KPISelector
                        isVisible={isPopoverVisible}
                        setIsVisible={setIsPopoverVisible}
                        kpiList={metricKPIsForSelector || []}
                        onSubmit={onMetricKPISelectHandler}
                        buttonName={'Add / Remove KPI'}
                        searchPlaceholder="Search KPI..."
                        variableName="Spend Metric"
                        disableMinimumOneSelectionCondition
                    />
                </div>
            </div>
        </>
    );
};

export default SpendDetails;
